import { Modal, ModalVariant } from '@farmasiet/frontendcore-reactcomponents';
import { observer } from 'mobx-react';
import React, { useEffect, useState } from 'react';

import { PictureProfile } from '../../../../ResizedImage/PictureProfile.csharp';
import { getTranslation, keys } from '../../../../Resources/Translations';
import { ExpectedDeliveryDateViewModel } from '../../../Purchase/Checkout/Delivery/DeliveryMethods/Common/ExpectedDelivery/ExpectedDeliveryDateViewModel.csharp';
import { expectedDeliveryDate } from '../ExpectedDeliveryDate';
import { ExpectedDeliveryDateInput } from './ExpectedDeliveryDateInput';
import { ExpectedDeliveryDateResults } from './ExpectedDeliveryDateResults';

interface IExpectedDeliveryDateModalProps {
    isOpen: boolean;
    onClose: () => void;
    logoPictureProfile: PictureProfile;
    deliveryInfoUrl: string;
    initialPostalCode: string | null;
    getDeliveryMethods: (postalCode: string) => Promise<ExpectedDeliveryDateViewModel[]>;
    logCustomEvent: (name: string, properties?: { [key: string]: string }) => void;
    checkDeliveryDateOnOpen?: boolean;
}

export enum ExpectedDeliveryDateStep {
    Init,
    Loading,
    SelectMethod,
    NoAvailableMethods,
    UnexpectedError
}

export const ExpectedDeliveryDateModal = observer((props: IExpectedDeliveryDateModalProps) => {
    const [step, setStep] = useState(ExpectedDeliveryDateStep.Init);

    const initialPostalCode = expectedDeliveryDate.postalCode || props.initialPostalCode;

    useEffect(() => {
        if (
            initialPostalCode &&
            expectedDeliveryDate.deliveryMethods?.methods &&
            expectedDeliveryDate.deliveryMethods.methods.length > 0 &&
            expectedDeliveryDate.deliveryMethods.postalCode === initialPostalCode
        ) {
            setStep(ExpectedDeliveryDateStep.SelectMethod);
        }
    }, []);

    useEffect(() => {
        if (props.checkDeliveryDateOnOpen && props.isOpen && initialPostalCode) {
            checkDeliveryDate(initialPostalCode);
        }
    }, [props.isOpen]);

    const checkDeliveryDate = async (postalCode: string) => {
        expectedDeliveryDate.setPostalCode(postalCode);

        try {
            setStep(ExpectedDeliveryDateStep.Loading);

            const results = await props.getDeliveryMethods(postalCode);

            if (results?.length > 0) {
                expectedDeliveryDate.setDeliveryMethods(results, postalCode);
                if (expectedDeliveryDate.selectedDeliveryMethod) {
                    const updatedSelectedMethod = results.find((x) => x.methodName === expectedDeliveryDate.selectedDeliveryMethod?.methodName);
                    updatedSelectedMethod && expectedDeliveryDate.selectDeliveryMethod(updatedSelectedMethod);
                }
                return setStep(ExpectedDeliveryDateStep.SelectMethod);
            }

            setStep(ExpectedDeliveryDateStep.NoAvailableMethods);
        } catch (err) {
            if ((err as Response)?.status === 400) {
                setStep(ExpectedDeliveryDateStep.NoAvailableMethods);
                return expectedDeliveryDate.setDeliveryMethods([], null);
            }

            setStep(ExpectedDeliveryDateStep.UnexpectedError);
        }
    };

    return (
        <Modal
            variant={ModalVariant.Center}
            className="ExpectedDeliveryDateModal"
            contentClassName="ExpectedDeliveryDateResults__Content"
            heading={getTranslation(keys['expectedDeliveryDate.deliveryDate'])}
            closeLabel={getTranslation(keys['modal.close'])}
            isVisible={props.isOpen}
            onClose={props.onClose}
        >
            <div className="ExpectedDeliveryDateModal__PostalCodeWrapper">
                <span className="ExpectedDeliveryDateModal__Info">{getTranslation(keys['expectedDeliveryDate.info'])}</span>

                <ExpectedDeliveryDateInput onSearch={checkDeliveryDate} initialPostalCode={props.initialPostalCode} />
            </div>

            <ExpectedDeliveryDateResults
                step={step}
                logoPictureProfile={props.logoPictureProfile}
                deliveryInfoUrl={props.deliveryInfoUrl}
                logCustomEvent={props.logCustomEvent}
            />
        </Modal>
    );
});
